/**
 * Approval Detail — Type-aware split layout
 *
 * Left: Action details + reasoning chain + type-specific editor
 * Right: Context panel with history timeline + summary + AI Chat
 *
 * Type-specific editors:
 * - email_draft: Rich text preview + recipient/subject edit
 * - crm_response: Response preview + lead info edit
 * - tool_call: Parameters grid with editable fields
 * - purchase_order: PO fields with budget context
 * - document: Diff view with version info
 * - access_change: Permission scope editor
 * - notification: Message preview + channel edit
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, CheckCircle, XCircle, AlertTriangle, Timer,
  Shield, Brain, FileCheck, Sparkles, Send,
  Mail, Users, Wrench, CreditCard, FileText, Bell, Lock, Zap,
  Clock, MessageSquare, Phone, Pencil, GitCommit, Activity,
  MessageCircle, Plus, Trash2, MoreVertical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AgentIcon } from '@/components/shared/icon-picker';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { useTrustUsers, shortId } from '@/lib/use-trust-entities';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ApprovalAIChat } from '@/components/approvals/approval-ai-chat';
import {
  useApprovalComments,
  usePostApprovalComment,
  isApprovalIdReal,
} from '@/lib/use-approval-comments';
import {
  APPROVAL_TYPE_LABELS,
  APPROVAL_LEVEL_STYLES,
  APPROVAL_PRIORITY_STYLES,
  type ApprovalRequest,
  type HistoryEvent,
} from '@/lib/mock-approvals-data';
import {
  useApproval,
  useApprovalRaw,
  useApproveDecision,
  useRejectDecision,
  useEditApproveDecision,
} from '@/lib/use-agent-approvals';
// Sprint 2 — schema-driven auto-rendered approval card. Gated on the API
// returning `contentSchemaJsonb`; existing layout below is preserved as the
// fallback. See docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.4.
import { AutoRenderedApprovalCard } from '@/components/approvals/AutoRenderedApprovalCard';
import type { LucideIcon } from 'lucide-react';

export const Route = createFileRoute('/approvals/$approvalId')({
  component: ApprovalDetailPage,
});

// History event icons
const EVENT_ICONS: Record<string, { icon: LucideIcon; color: string }> = {
  email: { icon: Mail, color: 'text-violet-500 dark:text-violet-400' },
  call: { icon: Phone, color: 'text-blue-500 dark:text-blue-400' },
  meeting: { icon: Users, color: 'text-emerald-500 dark:text-emerald-400' },
  note: { icon: Pencil, color: 'text-zinc-500 dark:text-zinc-400' },
  work_order: { icon: Wrench, color: 'text-cyan-500 dark:text-cyan-400' },
  alarm: { icon: AlertTriangle, color: 'text-red-500 dark:text-red-400' },
  reading: { icon: Activity, color: 'text-amber-500 dark:text-amber-400' },
  change: { icon: GitCommit, color: 'text-orange-500 dark:text-orange-400' },
  comment: { icon: MessageSquare, color: 'text-pink-500 dark:text-pink-400' },
  commit: { icon: GitCommit, color: 'text-indigo-500 dark:text-indigo-400' },
  message: { icon: Bell, color: 'text-cyan-500 dark:text-cyan-400' },
};

// SLA countdown hook
function useSLACountdown(expiresAt: string, isPending: boolean) {
  const [remaining, setRemaining] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!isPending) return;
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) { setRemaining('Expired'); setUrgent(true); setExpired(true); return; }
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      if (hrs > 0) setRemaining(`${hrs}h ${mins}m remaining`);
      else setRemaining(`${mins}m ${secs}s remaining`);
      setUrgent(diff < 900000); // < 15 min
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt, isPending]);

  return { remaining, urgent, expired };
}

// Comment type
interface ApprovalComment {
  id: string;
  author: string;
  content: string;
  timestamp: string;
}

let _commentId = 0;

function ApprovalDetailPage() {
  const { approvalId } = Route.useParams();
  const navigate = useNavigate();
  const { data: trustUsers = [] } = useTrustUsers();
  const formatDecidedBy = (id: string | null | undefined): string => {
    if (!id) return '—';
    const u = trustUsers.find(x => x.id === id);
    return u?.name || u?.email || `user:${shortId(id)}`;
  };
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectCategory, setRejectCategory] = useState('wrong_approach');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPreview, setEditedPreview] = useState('');
  const [aiRegenerateOpen, setAiRegenerateOpen] = useState(false);
  const [aiRegenerateFeedback, setAiRegenerateFeedback] = useState('');
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiChatContext, setAiChatContext] = useState('');
  const [aiChatField, setAiChatField] = useState('');
  const [rightPanel, setRightPanel] = useState<'context' | 'ai'>('context');
  const leftPanelRef = useRef<HTMLDivElement>(null);

  // Controlled parameter edits
  const [editedParams, setEditedParams] = useState<Record<string, string>>({});
  // Controlled email fields
  const [editedRecipients, setEditedRecipients] = useState('');
  const [editedSubject, setEditedSubject] = useState('');

  // Comments/notes — local thread for mock approvals; real thread
  // comes from A8 when the route id is a UUID (B5 wire-up).
  const [comments, setComments] = useState<ApprovalComment[]>([
    { id: 'c-init', author: 'System', content: 'Approval request created and assigned to your queue.', timestamp: new Date(Date.now() - 600000).toISOString() },
  ]);
  const [newComment, setNewComment] = useState('');

  const realApprovalThread = useApprovalComments(approvalId);
  const postRealComment = usePostApprovalComment(approvalId);
  const isRealApproval = isApprovalIdReal(approvalId);
  // Surface real comments alongside any local additions when on a
  // real approval. The JSX below keys off `comments`, so we prefer
  // the merged list.
  const displayedComments: ApprovalComment[] = isRealApproval
    ? (realApprovalThread.data?.comments ?? []).map((c) => ({
        id: c.id,
        author: c.isSystem ? 'System' : 'User',
        content: c.body,
        timestamp: c.createdAt,
      }))
    : comments;

  // Real-data: GET /agent-approvals/:id, adapted to ApprovalRequest VM.
  //
  // #1042 — pre-fix, the page rendered "Approval Not Found" immediately
  // while `realItem` was undefined, including during the initial fetch.
  // A slow GET (e.g. 2.5s) showed users a misleading 404 panel even
  // though the row existed. Surface a loading skeleton while pending and
  // a Not-Found state ONLY after the query settles without data.
  const { data: realItem, isLoading: itemLoading, isError: itemError } = useApproval(approvalId);
  const item = realItem ?? null;
  // Sprint 2 — raw row fetch (same query key, dedupes via cache) to read
  // `contentSchemaJsonb` for the auto-rendered card. Gated on presence: if
  // the API hasn't been updated to surface the column yet, this returns
  // undefined and the legacy editor below renders unchanged.
  const { data: rawApproval } = useApprovalRaw(approvalId);
  const hasContentSchema = !!rawApproval?.contentSchemaJsonb;
  const approveDecision = useApproveDecision();
  const rejectDecision = useRejectDecision();
  const editApproveDecision = useEditApproveDecision();

  // Initialize controlled state when item loads or edit mode toggles
  useEffect(() => {
    if (item && isEditing) {
      // Init params
      if (item.evidence.parameters) {
        const initial: Record<string, string> = {};
        for (const [k, v] of Object.entries(item.evidence.parameters)) {
          initial[k] = String(v);
        }
        setEditedParams(initial);
      }
      // Init preview
      setEditedPreview(item.evidence.preview ?? '');
      // Init email fields
      setEditedRecipients(item.evidence.recipients?.join(', ') ?? '');
      setEditedSubject(item.evidence.subject ?? '');
    }
  }, [item?.id, isEditing]);

  const sla = useSLACountdown(item?.expiresAt ?? '', item?.status === 'pending');

  // #1042 — show a loading skeleton while the detail GET is in flight
  // so users don't see "Not Found" during slow loads. Only when the
  // query has settled (data===undefined && !isLoading, or an explicit
  // error) do we show the Not-Found panel.
  if (!item) {
    if (itemLoading) {
      return (
        <div
          className="flex flex-col items-center justify-center h-full text-center p-6"
          aria-busy="true"
          aria-live="polite"
          data-testid="approval-detail-loading"
        >
          <Shield className="h-12 w-12 text-muted-foreground/30 mb-3 animate-pulse" />
          <h2 className="text-lg font-semibold text-muted-foreground">Loading approval…</h2>
        </div>
      );
    }
    return (
      <div
        className="flex flex-col items-center justify-center h-full text-center p-6"
        data-testid="approval-detail-not-found"
      >
        <Shield className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-lg font-semibold">Approval Not Found</h2>
        {itemError && (
          <p className="text-xs text-muted-foreground mt-1">
            We couldn't load this approval. It may have been deleted or you may not have access.
          </p>
        )}
        <Button variant="outline" className="mt-4" onClick={() => navigate({ to: '/approvals' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Inbox
        </Button>
      </div>
    );
  }

  const levelStyle = APPROVAL_LEVEL_STYLES[item.level];
  const priorityStyle = APPROVAL_PRIORITY_STYLES[item.priority];
  const isPending = item.status === 'pending';

  const handleApprove = () => {
    approveDecision.mutate(
      { id: item.id },
      {
        onSuccess: () => {
          toast.success(`Approved: ${item.action.title}`);
          navigate({ to: '/approvals' });
        },
        onError: (e) => toast.error('Approve failed', { description: e.message }),
      },
    );
  };

  const handleEditApprove = () => {
    const overrideArgs: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(editedParams)) overrideArgs[k] = v;
    if (editedPreview) overrideArgs.preview = editedPreview;
    const recipientsList = editedRecipients
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    editApproveDecision.mutate(
      {
        id: item.id,
        overrideArgs,
        editedRecipients: recipientsList.length ? recipientsList : null,
        editedSubject: editedSubject || null,
        editedBody: editedPreview || null,
        rationale: 'Approved with edits',
      },
      {
        onSuccess: () => {
          toast.success(`Approved with edits: ${item.action.title}`);
          navigate({ to: '/approvals' });
        },
        onError: (e) => toast.error('Edit-approve failed', { description: e.message }),
      },
    );
  };

  const handleReject = () => {
    rejectDecision.mutate(
      { id: item.id, rationale: rejectReason || rejectCategory, category: rejectCategory },
      {
        onSuccess: () => {
          toast.success(`Rejected (${rejectCategory}): ${item.action.title}`);
          setRejectOpen(false);
          navigate({ to: '/approvals' });
        },
        onError: (e) => toast.error('Reject failed', { description: e.message }),
      },
    );
  };

  const handleAiRegenerate = () => {
    // #596 — this previously faked a revision by appending a local
    // "[Revised based on your feedback]" marker (no LLM, no audit). We
    // still NEVER stub a revision into the field on a P1 human-in-the-loop
    // surface (truth-over-speed). The grounded AI Assist chat IS now wired
    // to a real backend (POST /agent-approvals/:id/ai-assist), so instead
    // of a flat refusal we hand the user's feedback to the live assistant:
    // open the AI panel, seed the question, and let Argus reason about the
    // requested change against the real approval. The human then applies
    // any edit explicitly via Edit + Approve with Edits.
    const feedback = aiRegenerateFeedback.trim();
    setAiRegenerateOpen(false);
    setAiRegenerateFeedback('');
    setAiChatField('');
    setAiChatContext(
      feedback ? `The approver wants this change: ${feedback}` : '',
    );
    setRightPanel('ai');
    setAiChatOpen(true);
    toast.info('Ask Argus about your requested change in the AI panel', {
      description:
        'Argus reviews it against this approval. Edits are applied by you via Edit → Approve with Edits — Argus never changes the field itself.',
    });
  };

  const handleAddComment = () => {
    const trimmed = newComment.trim();
    if (!trimmed) return;
    if (isRealApproval) {
      // Real approval: persist via A8.
      // #1106 — the server derives `authorId` from the authenticated
      // principal. Pre-fix the UI sent a placeholder authorId in the
      // body and the `.strict()` Zod schema on the server rejected the
      // whole request with `Unrecognized key: "authorId"`, so the
      // textbox stayed populated and the comment was lost. The hook no
      // longer accepts that field.
      postRealComment.mutate(
        { body: trimmed },
        {
          onSuccess: () => {
            setNewComment('');
            toast.success('Comment added');
          },
          onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message ?? 'Failed';
            toast.error('Comment failed', { description: msg });
          },
        },
      );
      return;
    }
    setComments(prev => [...prev, {
      id: `c-${++_commentId}`,
      author: 'You',
      content: trimmed,
      timestamp: new Date().toISOString(),
    }]);
    setNewComment('');
    toast.success('Comment added');
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const updateParam = (key: string, value: string) => {
    setEditedParams(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header — agent info + badges + SLA + action buttons */}
      <div className="border-b border-border/40 bg-background px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Back to Inbox" onClick={() => navigate({ to: '/approvals' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <AgentIcon iconName={item.agent.icon} colorName={item.agent.iconColor} size="sm" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate">{item.action.title}</h1>
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span>{item.agent.name} · {item.toolDisplayName}</span>
              {item.evidence.conversationTitle && (
                <>
                  <span>·</span>
                  <Link to="/approvals" className="text-primary hover:underline truncate max-w-[200px]" title={`From: ${item.evidence.conversationTitle}`}>
                    {item.evidence.conversationTitle}
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-2">
            <Badge variant="outline" className={cn('text-[9px]', priorityStyle.color)}>{priorityStyle.label}</Badge>
            <Badge variant="outline" className={cn('text-[9px]', levelStyle.color)}>{levelStyle.label}</Badge>
            {item.evidence.confidence != null && (
              <Badge variant="outline" className={cn('text-[9px]', item.evidence.confidence >= 0.8 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400')}>
                {Math.round(item.evidence.confidence * 100)}%
              </Badge>
            )}
          </div>

          {/* SLA countdown */}
          {isPending && sla.remaining && (
            <div className={cn('flex items-center gap-1 ml-2 text-[10px] font-medium', sla.urgent ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
              <Timer className="h-3 w-3" />
              {sla.remaining}
            </div>
          )}

          <div className="flex-1" />
          {!isPending && (
            <Badge variant="outline" className={cn('text-[9px]', item.status === 'approved' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/10 text-red-600 dark:text-red-400')}>
              {item.status} {item.decidedBy && `by ${formatDecidedBy(item.decidedBy)}`}
            </Badge>
          )}
        </div>
      </div>

      {/* Split layout */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT: Action + Editor + Reasoning */}
        <div ref={leftPanelRef} className="flex-1 overflow-auto p-4 space-y-4">
          {/* Action buttons — top of left panel, right-aligned */}
          {isPending && (
            <div className="flex items-center justify-end gap-2 -mt-1">
              <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={() => { setRightPanel('ai'); setAiChatOpen(true); }}>
                <Sparkles className="h-3 w-3" /> AI Assist
              </Button>
              <Button variant={isEditing ? 'secondary' : 'outline'} size="sm" className="gap-1 h-7 text-xs" onClick={() => setIsEditing(!isEditing)}>
                <Pencil className="h-3 w-3" /> {isEditing ? 'Preview' : 'Edit'}
              </Button>
              <Separator orientation="vertical" className="h-5" />
              <Button size="sm" className="gap-1 h-7 text-xs" onClick={handleApprove}>
                <CheckCircle className="h-3 w-3" /> Approve
              </Button>
              {isEditing && (
                <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={handleEditApprove}>
                  Approve with Edits
                </Button>
              )}
              <Button variant="outline" size="sm" className="gap-1 h-7 text-xs text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => setRejectOpen(true)}>
                <XCircle className="h-3 w-3" /> Reject
              </Button>
              {/* Disabled <button>s do not fire hover events, so the native
                  title tooltip never shows. Wrap in a title-bearing span
                  (which DOES receive hover) so the explanation is
                  discoverable. Real delegation is a separate feature build
                  (no POST /agent-approvals/:id/delegate endpoint yet). */}
              <span
                className="inline-flex cursor-not-allowed"
                title="Delegation is not yet implemented — no backend endpoint exists for /agent-approvals/:id/delegate"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 h-7 text-xs pointer-events-none"
                  disabled
                  tabIndex={-1}
                  aria-disabled="true"
                >
                  <Send className="h-3 w-3" /> Delegate
                </Button>
              </span>
            </div>
          )}

          {/* User's original request */}
          {item.evidence.userQuery && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-3 pb-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">User's Request</p>
                <p className="text-sm italic">"{item.evidence.userQuery}"</p>
              </CardContent>
            </Card>
          )}

          {/* Description */}
          <p className="text-sm text-muted-foreground">{item.action.description}</p>

          {/* Sprint 2 — schema-driven auto-rendered card. Renders only when
              the approval row carries `contentSchemaJsonb` (added Sprint 1).
              When absent, the legacy type-specific editor below renders as
              before. Workflow authors opt in by emitting a content_schema_jsonb
              on `tools.requireApproval`. */}
          {hasContentSchema && rawApproval && (
            <div
              data-testid="approval-auto-rendered"
              data-display-type-present={String(hasContentSchema)}
            >
              <AutoRenderedApprovalCard
                approval={{
                  id: rawApproval.id,
                  evidence: rawApproval.evidence,
                  decidedEvidence: rawApproval.decidedEvidence ?? null,
                  contentSchemaJsonb: rawApproval.contentSchemaJsonb,
                }}
                disabled={!isPending}
                onDecide={async ({ action, editedEvidence }) => {
                  // Bridge into the existing mutation surface so the
                  // auto-rendered card honors the same approve / reject /
                  // edit-approve plumbing as the legacy editor. Sprint 9
                  // owns the persist-evidence-edits flow; until then we
                  // pack the edits into `overrideArgs` so they round-trip.
                  if (action.decision === 'approve') {
                    approveDecision.mutate(
                      { id: item.id, rationale: 'Approved via auto-rendered card' },
                      {
                        onSuccess: () => {
                          toast.success(`Approved: ${action.label}`);
                          navigate({ to: '/approvals' });
                        },
                        onError: (e) =>
                          toast.error('Approve failed', { description: e.message }),
                      },
                    );
                  } else if (action.decision === 'reject') {
                    rejectDecision.mutate(
                      {
                        id: item.id,
                        rationale: action.label,
                        category: 'wrong_approach',
                      },
                      {
                        onSuccess: () => {
                          toast.success(`Rejected: ${action.label}`);
                          navigate({ to: '/approvals' });
                        },
                        onError: (e) =>
                          toast.error('Reject failed', { description: e.message }),
                      },
                    );
                  } else {
                    // 'custom' — Sprint 9 endpoint. For now route through
                    // edit-approve so the editedEvidence is at least
                    // preserved on the approval row.
                    editApproveDecision.mutate(
                      {
                        id: item.id,
                        overrideArgs: editedEvidence,
                        rationale: `Custom action: ${action.label}`,
                      },
                      {
                        onSuccess: () => {
                          toast.success(action.label);
                          navigate({ to: '/approvals' });
                        },
                        onError: (e) =>
                          toast.error(`${action.label} failed`, {
                            description: e.message,
                          }),
                      },
                    );
                  }
                }}
              />
            </div>
          )}

          {/* Type-specific editable preview */}
          {(item.evidence.preview || item.evidence.parameters) && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {item.type === 'email_draft' ? 'Email Draft' :
                     item.type === 'crm_response' ? 'Response Draft' :
                     item.type === 'notification' ? 'Message' :
                     item.type === 'document' ? 'Document Content' : 'Action Details'}
                  </CardTitle>
                  {isPending && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => setAiRegenerateOpen(true)}>
                      <Sparkles className="h-3 w-3" /> Regenerate with AI
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Email-specific fields */}
                {item.type === 'email_draft' && (
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-12">To:</span>
                      {isEditing ? (
                        <Input value={editedRecipients} onChange={e => setEditedRecipients(e.target.value)} className="h-7 text-xs flex-1" />
                      ) : (
                        <span className="text-xs flex-1">{item.evidence.recipients?.join(', ')}</span>
                      )}
                      {isPending && (
                        <button className="text-[9px] text-primary flex items-center gap-0.5" onClick={() => { setAiChatField('recipients'); setAiChatOpen(true); setRightPanel('ai'); }} title="AI help with recipients">
                          <Sparkles className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-12">Subject:</span>
                      {isEditing ? (
                        <Input value={editedSubject} onChange={e => setEditedSubject(e.target.value)} className="h-7 text-xs flex-1" />
                      ) : (
                        <span className="text-xs font-medium flex-1">{item.evidence.subject}</span>
                      )}
                      {isPending && (
                        <button className="text-[9px] text-primary flex items-center gap-0.5" onClick={() => { setAiChatField('subject'); setAiChatContext(item.evidence.subject ?? ''); setAiChatOpen(true); setRightPanel('ai'); }} title="AI help with subject">
                          <Sparkles className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </div>
                    <Separator />
                  </div>
                )}

                {/* Editable content preview */}
                {item.evidence.preview && (
                  isEditing ? (
                    <Textarea value={editedPreview} onChange={e => setEditedPreview(e.target.value)} rows={12} className="font-mono text-xs" />
                  ) : (
                    <div className="bg-muted/30 rounded-lg p-4 prose prose-sm dark:prose-invert max-w-none">
                      <MarkdownRenderer content={editedPreview || item.evidence.preview} />
                    </div>
                  )
                )}

                {/* Parameters grid (for tool calls) */}
                {item.evidence.parameters && !item.evidence.preview && (
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(item.evidence.parameters).map(([k, v]) => (
                      <div key={k} className="space-y-1 group/field">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{k}</p>
                          {isPending && (
                            <button
                              className="text-[9px] text-primary opacity-0 group-hover/field:opacity-100 transition-opacity flex items-center gap-0.5"
                              onClick={() => { setAiChatField(k); setAiChatContext(String(v)); setAiChatOpen(true); setRightPanel('ai'); }}
                              title={`Chat with AI about ${k}`}
                            >
                              <Sparkles className="h-2.5 w-2.5" /> AI
                            </button>
                          )}
                        </div>
                        {isEditing ? (
                          <Input
                            value={editedParams[k] ?? String(v)}
                            onChange={e => updateParam(k, e.target.value)}
                            className="h-8 text-xs font-mono"
                          />
                        ) : (
                          <p className="text-xs font-mono">{String(v)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Diff view */}
                {item.evidence.diff && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-red-500/5 rounded-lg p-3 border border-red-500/10">
                      <p className="text-[10px] font-semibold text-red-600 dark:text-red-400 mb-1">Before</p>
                      <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{item.evidence.diff.before}</pre>
                    </div>
                    <div className="bg-emerald-500/5 rounded-lg p-3 border border-emerald-500/10">
                      <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 mb-1">After</p>
                      <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{item.evidence.diff.after}</pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Reasoning Chain */}
          {item.evidence.reasoningChain && item.evidence.reasoningChain.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5"><Brain className="h-4 w-4" /> AI Decision Reasoning</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {item.evidence.reasoningChain.map(step => (
                    <div key={step.step} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">{step.step}</div>
                        {step.step < item.evidence.reasoningChain!.length && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-3 min-w-0">
                        <p className="text-xs font-medium">{step.action}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{step.result}</p>
                        {step.toolUsed && step.toolUsed !== 'llm_reasoning' && (
                          <Badge variant="secondary" className="text-[8px] font-mono mt-1">{step.toolUsed}</Badge>
                        )}
                        {step.toolUsed === 'llm_reasoning' && (
                          <Badge variant="outline" className="text-[8px] mt-1 bg-primary/5 text-primary">AI Reasoning</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Alternatives + Sources */}
          {item.evidence.alternativesConsidered && item.evidence.alternativesConsidered.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Alternatives Considered</CardTitle></CardHeader>
              <CardContent>
                {item.evidence.alternativesConsidered.map((alt, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs mb-1.5">
                    <XCircle className="h-3.5 w-3.5 text-muted-foreground/50 mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{alt}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {item.evidence.sourcesCited && item.evidence.sourcesCited.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Sources Cited</CardTitle></CardHeader>
              <CardContent>
                {item.evidence.sourcesCited.map((src, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs mb-1">
                    <FileCheck className="h-3 w-3 text-emerald-500 shrink-0" />
                    <span>{src}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Risk factors */}
          {item.evidence.riskFactors && item.evidence.riskFactors.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {item.evidence.riskFactors.map((f, i) => (
                <Badge key={i} variant="outline" className="text-[9px] bg-amber-500/5 text-amber-600 dark:text-amber-400 border-amber-500/20">
                  <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> {f}
                </Badge>
              ))}
            </div>
          )}

          {/* 2-person progress */}
          {item.level === 'two_person' && item.requiredApprovals && (
            <Card className="border-violet-500/20">
              <CardContent className="pt-4">
                <p className="text-sm font-medium mb-2">2-Person Rule: {item.approvals?.length ?? 0} of {item.requiredApprovals}</p>
                <Progress value={((item.approvals?.length ?? 0) / item.requiredApprovals) * 100} className="h-2 [&>div]:bg-violet-500" />
                {item.approvals?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 mt-2 text-xs">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                    <span>{a.userId} approved {formatTime(a.approvedAt)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Comments / Notes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5"><MessageCircle className="h-4 w-4" /> Comments & Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-3">
                {displayedComments.map(c => (
                  <div key={c.id} className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground shrink-0">
                      {c.author[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">{c.author}</span>
                        <span className="text-[10px] text-muted-foreground">{formatTime(c.timestamp)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.content}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Input
                  name="approval-new-comment"
                  aria-label="Add a comment"
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newComment.trim()) handleAddComment(); }}
                  placeholder="Add a comment or note..."
                  className="h-8 text-xs flex-1"
                />
                {/* #1045 — icon-only button MUST carry an accessible name so
                    screen readers + automated tests can find it and so the
                    disabled-when-empty guard is meaningfully announced. */}
                <Button
                  size="sm"
                  className="h-8 w-8 shrink-0"
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  aria-label="Add Comment"
                  title="Add Comment"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Already decided */}
          {!isPending && item.decidedBy && (
            <Card className={cn(item.status === 'approved' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5')}>
              <CardContent className="pt-4 flex items-center gap-3">
                {item.status === 'approved' ? <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /> : <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
                <div>
                  <p className="text-sm font-medium capitalize">{item.status} by {formatDecidedBy(item.decidedBy)}</p>
                  {item.decisionReason && <p className="text-xs text-muted-foreground mt-0.5">{item.decisionReason}</p>}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Context / AI Chat Panel */}
        <div className="w-[340px] shrink-0 border-l flex flex-col bg-card">
          {/* Panel toggle tabs */}
          <div className="flex items-center border-b shrink-0 px-2 py-1.5">
            <button onClick={() => setRightPanel('context')} className={cn('px-3 py-1 rounded-md text-xs font-medium transition-colors', rightPanel === 'context' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              Context
            </button>
            <button onClick={() => { setRightPanel('ai'); setAiChatOpen(true); }} className={cn('px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1', rightPanel === 'ai' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              <Sparkles className="h-3 w-3" /> AI Chat
            </button>
          </div>

          {/* AI Chat Panel */}
          {rightPanel === 'ai' && (
            <ApprovalAIChat
              open={true}
              onClose={() => setRightPanel('context')}
              approvalId={approvalId}
              context={aiChatContext || `Approval: ${item.action.title}\nAgent: ${item.agent.name}\nTool: ${item.toolDisplayName}\nPriority: ${item.priority}\nRisk: ${Math.round(item.riskScore)}%${item.evidence.userQuery ? `\nUser asked: "${item.evidence.userQuery}"` : ''}`}
              fieldName={aiChatField}
              onApplyUpdate={(field, value) => {
                toast.success(`AI updated the ${field} — review the changes on the left`);
                if (field === 'preview') setEditedPreview(value);
              }}
              className="flex-1 max-h-none rounded-none border-0 shadow-none"
            />
          )}

          {/* Context Panel */}
          {rightPanel === 'context' && (
            <div className="flex-1 overflow-auto">
              {/* Context summary */}
              {item.evidence.contextSummary && item.evidence.contextSummary.length > 0 && (
                <div className="p-4 border-b">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Context</h3>
                  <div className="space-y-2">
                    {item.evidence.contextSummary.map((s, i) => (
                      <div key={i}>
                        <p className="text-[10px] text-muted-foreground">{s.label}</p>
                        <p className="text-xs font-medium">{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related records */}
              {item.evidence.relatedRecords && item.evidence.relatedRecords.length > 0 && !item.evidence.contextSummary && (
                <div className="p-4 border-b">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Related Information</h3>
                  <div className="space-y-2">
                    {item.evidence.relatedRecords.map((r, i) => (
                      <div key={i}>
                        <p className="text-[10px] text-muted-foreground">{r.label}</p>
                        <p className="text-xs">{r.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* History timeline */}
              {item.evidence.contextHistory && item.evidence.contextHistory.length > 0 && (
                <div className="p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">History</h3>
                  <div className="space-y-0">
                    {item.evidence.contextHistory.map((event, i) => {
                      const eventConfig = EVENT_ICONS[event.type] ?? { icon: Clock, color: 'text-muted-foreground' };
                      const EventIcon = eventConfig.icon;
                      return (
                        <div key={i} className="flex gap-3 pb-4">
                          <div className="flex flex-col items-center">
                            <div className={cn('w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0')}>
                              <EventIcon className={cn('h-3 w-3', eventConfig.color)} />
                            </div>
                            {i < item.evidence.contextHistory!.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                          </div>
                          <div className="min-w-0 pt-0.5">
                            <p className="text-xs font-medium">{event.title}</p>
                            {event.description && <p className="text-[10px] text-muted-foreground mt-0.5">{event.description}</p>}
                            <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground/60">
                              <span>{formatTime(event.timestamp)}</span>
                              {event.actor && <><span>·</span><span>{event.actor}</span></>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty right panel fallback */}
              {!item.evidence.contextHistory && !item.evidence.contextSummary && !item.evidence.relatedRecords && (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <Clock className="h-8 w-8 text-muted-foreground/20 mb-2" />
                  <p className="text-xs text-muted-foreground">No additional context available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject: {item.action.title}</DialogTitle>
            <DialogDescription>The agent will be notified with your feedback and may propose a revised action.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Reason<span className="text-red-500 ml-0.5">*</span></Label>
              <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="text-xs" placeholder="Why are you rejecting? What should the agent do differently?" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={rejectCategory} onValueChange={setRejectCategory}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wrong_approach">Wrong approach — try different method</SelectItem>
                  <SelectItem value="wrong_params">Wrong parameters — needs correction</SelectItem>
                  <SelectItem value="safety_concern">Safety concern — too risky</SelectItem>
                  <SelectItem value="policy_violation">Policy violation</SelectItem>
                  <SelectItem value="not_needed">Not needed — cancel this action</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              variant="outline"
              disabled
              title="Send-back-for-revision is not yet wired — use Reject with rationale until the agent-feedback endpoint lands."
            >
              Send Back for Revision
            </Button>
            <Button variant="destructive" disabled={!rejectReason.trim()} onClick={handleReject}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Regenerate dialog */}
      <Dialog open={aiRegenerateOpen} onOpenChange={setAiRegenerateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Regenerate with AI</DialogTitle>
            <DialogDescription>Tell the AI what to change and it will generate a revised version.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">What should be different?</Label>
            <Textarea value={aiRegenerateFeedback} onChange={e => setAiRegenerateFeedback(e.target.value)} rows={3} className="text-xs" placeholder="e.g., Make the tone more formal, add pricing details, change the priority to medium..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiRegenerateOpen(false)}>Cancel</Button>
            <Button disabled={!aiRegenerateFeedback.trim()} onClick={handleAiRegenerate} className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
